import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// Drop a peer not heard from for PEER_TTL_MS (a crashed server process never
// sends its "leave"). Peers are re-announced by the SERVER every 25s while
// their socket is open (PresenceConsumer._heartbeat_loop) -- deliberately not
// by a browser timer, which Chrome throttles and Edge's sleeping tabs freeze
// in background tabs, making those users vanish from everyone else's screen.
const PEER_TTL_MS = 70000;
const PRUNE_MS = 10000;
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY_MS = 3000;

const fetchWsAuthToken = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}ws-auth-token/`, { withCredentials: true });
    return res.data?.token || null;
  } catch {
    return null;
  }
};

// Same host/path resolution as usePermissionsWebSocket.
const getWebSocketURL = (authToken = null) => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  let host = window.location.host;

  if (process.env.NODE_ENV === 'development') {
    host = `${window.location.hostname}:8000`;
  }

  const BASE_URL = process.env.REACT_APP_BASE_URL || '/';
  let path = '';

  if (BASE_URL.startsWith('http://') || BASE_URL.startsWith('https://')) {
    try {
      path = new URL(BASE_URL).pathname.replace(/\/$/, '');
    } catch (e) {
      path = '';
    }
  } else {
    path = BASE_URL === '/' ? '' : BASE_URL.replace(/\/$/, '');
  }

  let wsUrl = `${protocol}//${host}${path}/ws/presence/`;
  if (authToken) wsUrl += `?auth_token=${authToken}`;
  return wsUrl;
};


const sameFocus = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * The live presence channel for `page` (a pathname), over /ws/presence/.
 *
 * One socket per tab, kept open across navigation; a route change just sends
 * a new `join`. Returns:
 *   - `others`: one entry per person on the page (a user with two tabs appears
 *     once), in the order they arrived, excluding `selfEmail`;
 *   - `peers`: one entry per other tab -- `{ channel, member, focus }`, where
 *     focus is `{ grid, rows, editing }` (the table rows that tab is on) or null;
 *   - `records`: one entry per other tab in the same TEAMSPACE (any page) that
 *     is editing a shared record -- `{ channel, member, record: { grid, id } }`
 *     (today: time entries, editable from both Time Entries and Calendar);
 *   - `setFocus(grid, rows, editingId)` / `clearFocus(grid)`: announce this
 *     tab's own row focus. Only one grid's focus is held at a time; clearFocus
 *     is a no-op unless `grid` owns it, so an idle grid can't wipe a busy one.
 *
 * Both arrays keep their identity across heartbeats that change nothing, so
 * consumers only re-render when someone arrives, leaves or moves.
 */
export const usePresenceChannel = (page, { enabled = true, selfEmail = null } = {}) => {
  // channel name -> { member, focus, lastSeen, firstSeen }
  const [peers, setPeers] = useState({});
  // channel name -> { member, record, lastSeen } -- teamspace-wide, NOT reset on
  // a page change within the teamspace (the server sends presence_records_reset
  // when the teamspace itself changes).
  const [records, setRecords] = useState({});

  const wsRef = useRef(null);
  const pageRef = useRef(page);
  pageRef.current = page;
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  // Bumped by every connect() and every cleanup. connect() awaits the auth token
  // before opening, so without this a superseded call (StrictMode's double
  // effect, a quick reconnect) would still open a second, orphaned socket that
  // stays joined to whatever page it saw first -- a ghost of you on that page.
  const generationRef = useRef(0);
  // This tab's row focus: { grid, rows, editing } or null. Re-sent after every
  // join, because the server clears focus when a tab (re)joins a page.
  const focusRef = useRef(null);

  const send = useCallback((message) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(message));
    }
  }, []);

  const sendFocus = useCallback(() => {
    if (focusRef.current) send({ type: 'focus', ...focusRef.current });
  }, [send]);

  const setFocus = useCallback((grid, rows, editing = null) => {
    const ids = Array.from(new Set((rows || []).filter((r) => r != null && r !== '').map(String)));
    if (editing != null && !ids.includes(String(editing))) ids.unshift(String(editing));
    if (ids.length === 0) {
      if (focusRef.current?.grid === grid) {
        focusRef.current = null;
        send({ type: 'focus', grid, rows: [] });
      }
      return;
    }
    const next = { grid, rows: ids, editing: editing != null ? String(editing) : null };
    if (sameFocus(next, focusRef.current)) return;
    focusRef.current = next;
    send({ type: 'focus', ...next });
  }, [send]);

  const clearFocus = useCallback((grid) => setFocus(grid, []), [setFocus]);

  const connect = useCallback(async () => {
    if (!enabled) return;
    const generation = ++generationRef.current;
    if (wsRef.current) {
      wsRef.current.close(1000);
      wsRef.current = null;
    }

    try {
      const authToken = await fetchWsAuthToken();
      if (generation !== generationRef.current) return;
      const ws = new WebSocket(getWebSocketURL(authToken));

      ws.onopen = () => {
        reconnectAttempts.current = 0;
        if (pageRef.current) {
          ws.send(JSON.stringify({ type: 'join', page: pageRef.current }));
          if (focusRef.current) ws.send(JSON.stringify({ type: 'focus', ...focusRef.current }));
        }
      };

      ws.onmessage = (event) => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch (e) {
          return;
        }
        if (data.type === 'presence_reset') {
          setPeers({});
        } else if (data.type === 'presence_here' && data.channel && data.member) {
          const now = Date.now();
          setPeers((prev) => ({
            ...prev,
            [data.channel]: {
              member: data.member,
              focus: data.focus || null,
              lastSeen: now,
              firstSeen: prev[data.channel]?.firstSeen ?? now,
            },
          }));
        } else if (data.type === 'presence_leave' && data.channel) {
          setPeers((prev) => {
            if (!prev[data.channel]) return prev;
            const next = { ...prev };
            delete next[data.channel];
            return next;
          });
        } else if (data.type === 'presence_record' && data.channel) {
          setRecords((prev) => {
            if (!data.record || !data.member) {
              if (!prev[data.channel]) return prev;
              const next = { ...prev };
              delete next[data.channel];
              return next;
            }
            return { ...prev, [data.channel]: { member: data.member, record: data.record, lastSeen: Date.now() } };
          });
        } else if (data.type === 'presence_records_reset') {
          setRecords({});
        }
      };

      ws.onerror = () => {};

      ws.onclose = (event) => {
        if (wsRef.current === ws) {
          setPeers({});
          setRecords({});
        }
        if (event.code !== 1000 && event.code !== 4001 && reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttempts.current += 1;
          reconnectTimeoutRef.current = setTimeout(() => connect(), RECONNECT_DELAY_MS);
        }
      };

      wsRef.current = ws;
    } catch (e) {
      // Connection failed; presence simply stays empty.
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled) connect();
    return () => {
      generationRef.current += 1;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (wsRef.current) {
        wsRef.current.close(1000);
        wsRef.current = null;
      }
      setPeers({});
      setRecords({});
    };
  }, [enabled, connect]);

  // Route change: move this tab to the new page's group. Runs after the new
  // page's own effects (children first), so a grid that already set its focus
  // gets it re-sent after the join that would otherwise clear it.
  useEffect(() => {
    setPeers({});
    if (page) {
      send({ type: 'join', page });
      sendFocus();
    } else {
      send({ type: 'leave' });
    }
  }, [page, send, sendFocus]);

  // Expire peers whose tab died without saying goodbye.
  useEffect(() => {
    const interval = setInterval(() => {
      const cutoff = Date.now() - PEER_TTL_MS;
      const prune = (prev) => {
        const stale = Object.keys(prev).filter((channel) => prev[channel].lastSeen < cutoff);
        if (stale.length === 0) return prev;
        const next = { ...prev };
        stale.forEach((channel) => delete next[channel]);
        return next;
      };
      setPeers(prune);
      setRecords(prune);
    }, PRUNE_MS);
    return () => clearInterval(interval);
  }, []);

  // Everything but lastSeen: identical after a heartbeat that changed nothing.
  const ordered = Object.entries(peers)
    .sort(([, a], [, b]) => a.firstSeen - b.firstSeen)
    .filter(([, { member }]) => member?.email && member.email !== selfEmail)
    .map(([channel, { member, focus }]) => ({ channel, member, focus }));
  const recordList = Object.entries(records)
    .filter(([, { member }]) => member?.email && member.email !== selfEmail)
    .map(([channel, { member, record }]) => ({ channel, member, record }));
  const signature = JSON.stringify([ordered, recordList]);
  const viewsRef = useRef({ signature: null, views: null });
  if (viewsRef.current.signature !== signature) {
    const byEmail = new Map();
    // Latest info wins (a newer tab may carry a newer picture), first arrival keeps its place.
    ordered.forEach(({ member }) => byEmail.set(member.email, member));
    viewsRef.current = {
      signature,
      views: { others: Array.from(byEmail.values()), peers: ordered, records: recordList },
    };
  }
  const { views } = viewsRef.current;

  return useMemo(
    () => ({ others: views.others, peers: views.peers, records: views.records, setFocus, clearFocus }),
    [views, setFocus, clearFocus],
  );
};

/** Just the other people on `page` -- see usePresenceChannel. */
export const usePagePresence = (page, options) => usePresenceChannel(page, options).others;
